import React, { useContext } from "react"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { Link, Typography } from "@carrotfertility/carotene-core"
import { Link as RouterLink } from "react-router-dom"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"
import { useConvertTranslatedLabel } from "#/services/common-forms/useConvertTranslatedLabel"
import { FormSelectOption } from "@carrotfertility/carotene-core-x"
import { SelectWithTileStep } from "#/components/carrot-card-cost-share/steps/variants/SelectWithTileStep"
import { Paths } from "#/utils/Paths"
import { CarrotCardCostSharePreAuthContext } from "#/components/carrot-card-cost-share/state/CarrotCardCostSharePreAuthContext"
import { JourneySubTypeValues } from "#/components/carrot-card-cost-share/steps/shared/stepOptionValues"
import { CarrotRxRequiredBox } from "#/components/carrot-card-cost-share/steps/shared/CarrotRxRequiredBox"
import { useFlags } from "launchdarkly-react-client-sdk"

type WhatKindLowTestosteroneOption = {
  label: MessageDescriptor
  value: string
}

const baseWhatKindLowTestosteroneOptions: WhatKindLowTestosteroneOption[] = [
  {
    label: defineMessage({ defaultMessage: "Medications" }),
    value: JourneySubTypeValues.MEDICATIONS
  },
  {
    label: defineMessage({ defaultMessage: "In-person care" }),
    value: JourneySubTypeValues.IN_PERSON_CARE
  },
  {
    label: defineMessage({ defaultMessage: "Online care" }),
    value: JourneySubTypeValues.ONLINE_CARE
  },
  {
    label: defineMessage({ defaultMessage: "Nutrition counseling with a registered dietitian" }),
    value: JourneySubTypeValues.NUTRITION_COUNSELING
  }
]

const pelvicFloorTherapyOption: WhatKindLowTestosteroneOption = {
  label: defineMessage({ defaultMessage: "Pelvic floor physical therapy" }),
  value: JourneySubTypeValues.PELVIC_FLOOR_THERAPY
}

export function WhatKindLowTestosteroneStep(): JSX.Element {
  const intl = useIntl()
  const { send } = useStateMachine()
  const {
    dispatchFormState,
    formState: { journeySubType }
  } = useContext(CarrotCardCostSharePreAuthContext)
  const { pelvicFloorTherapyReimbursementLaunched } = useFlags()

  const whatKindLowTestosteroneOptions = pelvicFloorTherapyReimbursementLaunched
    ? [
        ...baseWhatKindLowTestosteroneOptions.slice(0, 3),
        pelvicFloorTherapyOption,
        ...baseWhatKindLowTestosteroneOptions.slice(3)
      ]
    : baseWhatKindLowTestosteroneOptions

  const formattedOptions = useConvertTranslatedLabel(whatKindLowTestosteroneOptions) as FormSelectOption[]

  function onSubmit(formValues: { [key: string]: string }): void {
    dispatchFormState({ type: "UPDATE_FIELD", field: "journeySubType", value: formValues.journeySubType })
    send(formValues.journeySubType === JourneySubTypeValues.PELVIC_FLOOR_THERAPY ? formValues.journeySubType : "")
  }

  return (
    <SelectWithTileStep
      title={intl.formatMessage({ defaultMessage: "What kind of low testosterone expense is this?" })}
      description={
        <Typography sx={(theme) => ({ marginBlockEnd: theme.tokens.spacing.lg })}>
          <FormattedMessage
            defaultMessage="If you're not sure or don't see something listed, <link>contact us</link>."
            values={{
              link: (linkContent) => (
                <Link
                  component={RouterLink}
                  to={Paths.CARE_TEAM}
                  color="inherit"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {linkContent}
                </Link>
              )
            }}
          />
        </Typography>
      }
      selectName="journeySubType"
      options={formattedOptions}
      onSubmit={onSubmit}
      defaultValue={journeySubType}
    >
      <CarrotRxRequiredBox name="journeySubType" />
    </SelectWithTileStep>
  )
}
